import { useEffect, useRef, useState } from 'react';

/**
 * Hook for monitoring component performance
 * Tracks render times and provides performance insights
 */
export const usePerformanceMonitor = (componentName) => {
  const renderCount = useRef(0);
  const renderTimes = useRef([]);
  const [averageRenderTime, setAverageRenderTime] = useState(0);
  const [isSlowRender, setIsSlowRender] = useState(false);

  useEffect(() => {
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      
      renderCount.current += 1;
      renderTimes.current.push(renderTime);
      
      // Keep only last 10 render times
      if (renderTimes.current.length > 10) {
        renderTimes.current.shift();
      }
      
      // Calculate average render time
      const average = renderTimes.current.reduce((a, b) => a + b, 0) / renderTimes.current.length;
      setAverageRenderTime(average);
      
      // Flag slow renders (>16ms for 60fps)
      setIsSlowRender(average > 16);
      
      // Log performance in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`${componentName} render #${renderCount.current}: ${renderTime.toFixed(2)}ms (avg: ${average.toFixed(2)}ms)`);
      }
    };
  });

  return {
    renderCount: renderCount.current,
    averageRenderTime,
    isSlowRender,
    renderTimes: renderTimes.current
  };
};

/**
 * Hook for measuring API call performance
 */
export const useApiPerformanceMonitor = () => {
  const [apiCalls, setApiCalls] = useState([]);
  const [slowCalls, setSlowCalls] = useState([]);

  const measureApiCall = async (apiCall, callName) => {
    const startTime = performance.now();
    
    try {
      const result = await apiCall();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const callData = {
        name: callName,
        duration,
        timestamp: new Date().toISOString(),
        success: true
      };
      
      setApiCalls(prev => [...prev.slice(-9), callData]);
      
      // Flag slow API calls (>1000ms)
      if (duration > 1000) {
        setSlowCalls(prev => [...prev.slice(-4), callData]);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`API call ${callName}: ${duration.toFixed(2)}ms`);
      }
      
      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const callData = {
        name: callName,
        duration,
        timestamp: new Date().toISOString(),
        success: false,
        error: error.message
      };
      
      setApiCalls(prev => [...prev.slice(-9), callData]);
      
      throw error;
    }
  };

  return {
    apiCalls,
    slowCalls,
    measureApiCall
  };
};

/**
 * Hook for monitoring memory usage
 */
export const useMemoryMonitor = () => {
  const [memoryInfo, setMemoryInfo] = useState(null);

  useEffect(() => {
    const updateMemoryInfo = () => {
      if ('memory' in performance) {
        setMemoryInfo({
          usedJSHeapSize: performance.memory.usedJSHeapSize,
          totalJSHeapSize: performance.memory.totalJSHeapSize,
          jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
          usage: (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100
        });
      }
    };

    updateMemoryInfo();
    const interval = setInterval(updateMemoryInfo, 5000);

    return () => clearInterval(interval);
  }, []);

  return memoryInfo;
};

export default usePerformanceMonitor;
